import React, { useState } from 'react';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import TablePagination from '@material-ui/core/TablePagination';

import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { UiFilterToggle } from '../../ui/ui-filter-toggle';

import { employeeName } from '../../employees/utils/employee-name';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { sortAlphabetically, SORT_ORDER } from '../../util/sort-functions';
import { OrgSelect } from '../../widgets/org-select';

type EmployeeAssignmentHandler = (
  employeeId: number,
) => (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => void;

type InputProps = {
  orgs: Org[];
  assignmentConflicts: { [employeeGroupId: number]: Set<number> };
  explicitEmployeeAssignments: { [employeeId: number]: number };
  employeeById: (employeeId: number) => Employee;
  handleExplicitEmployeeAssignment: EmployeeAssignmentHandler;
};

const EmployeeConflictResolutionRow = ({
  employee,
  selectedOrg,
  orgIds,
  orgArr,
  handleExplicitEmployeeAssignment,
}: {
  employee: Employee;
  selectedOrg: number;
  orgIds: Set<number>;
  orgArr: Org[];
  handleExplicitEmployeeAssignment: EmployeeAssignmentHandler;
}) => (
  <div
    key={`${employee.employeeId}-card`}
    className="card u-margin-top u-display-flex u-justifyContent-spaceBetween"
  >
    <Accordion className="u-width-80" style={{ boxShadow: '0 0 0 0' }}>
      <AccordionSummary expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}>
        <div className="typo-title">{employeeName(employee)}</div>
      </AccordionSummary>
      <AccordionDetails className="u-display-flex u-flexWrap-wrap">
        {employee.EmployeeGroups.map((group) => (
          <Chip
            className="u-margin-top-xs u-margin-bottom-xs u-margin-right-sm"
            key={group.employeeGroupId}
            size="small"
            color="primary"
            label={group.name}
          />
        ))}
      </AccordionDetails>
    </Accordion>
    <OrgSelect
      className="u-width-20 u-margin-right"
      disableUnderline
      extraOptions={[
        <MenuItem key="None" value="None">
          <div className="orgDropdown-nameContainer">
            <div className="orgDropdown-name">Select an organization</div>
          </div>
        </MenuItem>,
      ]}
      onChange={handleExplicitEmployeeAssignment(employee.employeeId)}
      orgOptions={orgArr.filter((o) => orgIds.has(o.org_id))}
      value={selectedOrg || 'None'}
    />
  </div>
);

export const ResolveConflictsTab = ({
  orgs,
  assignmentConflicts,
  explicitEmployeeAssignments,
  employeeById,
  handleExplicitEmployeeAssignment,
}: InputProps) => {
  const pageSize = 10;
  const [assignmentConflictOffset, setAssignmentConflictOffset] = useState<number>(0);
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
  const [shouldSortEmployeesByLastName, setShouldSortEmployeesByLastName] = useState<boolean>(true);

  const handleAssignmentConflictPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setAssignmentConflictOffset(page * pageSize);
  };

  const handleEmployeeNameFilterChange = (updatedEmployeeNameFilter: string) => {
    setEmployeeNameFilter(updatedEmployeeNameFilter);
  };

  const handleShouldSortEmployeesByLastNameChange = () => {
    setShouldSortEmployeesByLastName(!shouldSortEmployeesByLastName);
  };

  const getFilteredAssignmentConflicts = () =>
    Object.keys(assignmentConflicts).reduce((acc, employeeIdAsString) => {
      const employeeId = parseInt(employeeIdAsString, 10);
      const employee = employeeById(employeeId);
      if (
        `${employee.Contact.Person.firstName} ${employee.Contact.Person.lastName}`.match(
          new RegExp(employeeNameFilter, 'i'),
        )
      ) {
        acc[employeeId] = assignmentConflicts[employeeId];
      }
      return acc;
    }, {} as typeof assignmentConflicts);

  const filteredAssignmentConflicts = getFilteredAssignmentConflicts();
  const sortedFilteredAssignmentConflictEntries = Object.entries(filteredAssignmentConflicts).sort(
    sortAlphabetically(([stringEmployeeId]) => {
      const employeeId = parseInt(stringEmployeeId, 10);
      const employee = employeeById(employeeId);
      return shouldSortEmployeesByLastName
        ? employee.Contact.Person.lastName
        : employee.Contact.Person.firstName;
    }, SORT_ORDER.ASC),
  );
  const paginatedAssignmentConflictEntries =
    sortedFilteredAssignmentConflictEntries.length > pageSize
      ? sortedFilteredAssignmentConflictEntries.slice(
          assignmentConflictOffset,
          assignmentConflictOffset + pageSize,
        )
      : sortedFilteredAssignmentConflictEntries;

  return (
    <>
      <div className="card-head-no-border typo-subtitle u-margin-top">
        These employees are members of groups that were assigned to different organizations. You can
        explicitly assign them to organizations here.
      </div>
      <div className="card">
        <div className="card-body u-display-flex u-alignItems-flexStart">
          <UiFilterTextField
            label="Employee Name"
            value={employeeNameFilter}
            onChange={handleEmployeeNameFilterChange}
          />
          <UiFilterToggle
            className="u-margin-left"
            label={`Sort by ${shouldSortEmployeesByLastName ? 'First' : 'Last'} Name`}
            value={shouldSortEmployeesByLastName}
            onChange={handleShouldSortEmployeesByLastNameChange}
          />
        </div>
      </div>
      {paginatedAssignmentConflictEntries.map(([stringEmployeeId, orgIds]) => {
        const employeeId = parseInt(stringEmployeeId, 10);
        const employee = employeeById(employeeId);

        const selectedOrg = explicitEmployeeAssignments[employeeId];
        return (
          <EmployeeConflictResolutionRow
            employee={employee}
            selectedOrg={selectedOrg}
            orgIds={orgIds}
            orgArr={orgs}
            handleExplicitEmployeeAssignment={handleExplicitEmployeeAssignment}
          />
        );
      })}
      {paginatedAssignmentConflictEntries.length >= pageSize && (
        <TablePagination
          component="div"
          count={Object.keys(filteredAssignmentConflicts).length}
          rowsPerPage={pageSize}
          page={assignmentConflictOffset / pageSize}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onPageChange={handleAssignmentConflictPageChange}
          rowsPerPageOptions={[pageSize]}
        />
      )}
    </>
  );
};
